/**
 * Geometrie des Startbilds «Beleglauf» (AB/RE-Portal).
 *
 * Ein Koordinatensystem für alles: die Bildpunkte des Fotos (1500 x 999,
 * Dällikon - Dachrand gegen den Himmel).
 * Foto und Beleg-SVG liegen mit derselben Abbildung übereinander
 * (`preserveAspectRatio="xMidYMid slice"`, mittig = `object-fit: cover`),
 * deshalb sitzt der Beleg bei jedem Seitenverhältnis an derselben Stelle im Bild.
 *
 * ⚠️ Bei `cover` fallen oben und unten Bildpunkte weg: Auf 16:9 sind es je rund
 * 78 Punkte. Alles Wesentliche liegt deshalb zwischen y = 150 und y = 850.
 * Waagrecht ist der Beleg bewusst in die rechte Hälfte gesetzt - unten links
 * stehen Marke, Titel und Prozesskette. Senkrecht liegt er in der oberen
 * Bildhälfte, wo beim gewählten Foto nur Himmel ist: Der Beleg braucht eine
 * grosse ruhige Fläche, sonst liegt ein Formular auf einer Backsteinwand.
 */

export const BILD_BREITE = 1500;
export const BILD_HOEHE = 999;

/**
 * Aussenkante des Belegs.
 *
 * ⚠️ Nicht weiter nach rechts schieben: Auf hohen Schirmen (5:4) beschneidet
 * `cover` waagrecht, und die rechte Kante rückt an den Bildrand. Bei x + breite
 * = 1320 bleiben dort noch rund 55 Bildpunkte Luft.
 */
export const RAHMEN = { x: 690, y: 150, breite: 630, hoehe: 430 };

const INNEN_LINKS = RAHMEN.x + 30;
const INNEN_RECHTS = RAHMEN.x + RAHMEN.breite - 30;

export const BELEG = {
  innenLinks: INNEN_LINKS,
  innenRechts: INNEN_RECHTS,
  /** Trennlinie unter dem Kopfbereich. */
  kopfLinie: 234,
  /** Trennlinie unter der Spaltenüberschrift. */
  spaltenLinie: 274,
  /** Trennlinie über dem Summenblock. */
  summenLinie: 526,
  summenLinks: RAHMEN.x + 310,
  spaltenkopfY: 262,
  summeY: 556
};

/**
 * Kopfbereich: Lieferant und Adresse stehen als Striche da, nicht als Text.
 * Ein Formular erkennt man an seinem Aufbau - ausgeschriebene Fantasienamen
 * würden nur ablenken und wären auf dem Schirm ohnehin kaum zu lesen.
 */
export const KOPF_STRICHE = [
  { x: INNEN_LINKS, y: 188, breite: 286, stark: true },
  { x: INNEN_LINKS, y: 210, breite: 196, stark: false }
];

/** Rechts im Kopf: Belegnummer und Datum, echte Zeichen in Schreibmaschinenschrift. */
export const KOPF_TEXTE = [
  { y: 192, text: 'RE 261928' },
  { y: 214, text: '13.08.2026' }
];

const ZEILE_ERSTE_Y = 306;
const ZEILE_HOEHE = 38;

export interface Position {
  /** Mitte der Zeile in Bildpunkten. */
  y: number;
  /** Länge des Strichs, der die Bezeichnung vertritt. */
  breite: number;
  betrag: string;
  /**
   * Offene Zeile: Sie rastet NICHT ein, sondern bleibt bernsteinfarben stehen.
   * Genau das ist die Arbeit, um die es im Portal geht - ein Startbild, in dem
   * alles glatt durchläuft, würde das Werkzeug falsch beschreiben.
   */
  offen?: boolean;
}

export const POSITIONEN: Position[] = [
  { y: ZEILE_ERSTE_Y + 0 * ZEILE_HOEHE, breite: 300, betrag: "4'820.00" },
  { y: ZEILE_ERSTE_Y + 1 * ZEILE_HOEHE, breite: 238, betrag: "1'240.50" },
  { y: ZEILE_ERSTE_Y + 2 * ZEILE_HOEHE, breite: 274, betrag: "12'950.00", offen: true },
  { y: ZEILE_ERSTE_Y + 3 * ZEILE_HOEHE, breite: 206, betrag: '860.00' },
  { y: ZEILE_ERSTE_Y + 4 * ZEILE_HOEHE, breite: 322, betrag: "3'475.20" },
  { y: ZEILE_ERSTE_Y + 5 * ZEILE_HOEHE, breite: 252, betrag: "2'190.00" }
];

/** Kästchen links in der Zeile - Kantenlänge und Abstand zum Bezeichnungsstrich. */
export const KAESTCHEN = { groesse: 13, textAbstand: 22 };

/** Häkchen im Kästchen, relativ zu dessen linker oberer Ecke. */
export const HAKEN_PFAD = 'M2.8 6.8L5.6 9.6L10.6 3.6';

export const SUMME = { text: "25'535.70", waehrung: 'CHF' };

/** Der Stempel setzt sich zum Schluss - leicht schräg, wie von Hand. */
/**
 * Der Stempel steht links auf der Höhe des Summenblocks - er darf die Summe
 * nicht überdecken, und schräg gesetzt liest er sich wie von Hand aufgedrückt.
 */
export const STEMPEL = {
  x: 870,
  y: 556,
  breite: 230,
  hoehe: 42,
  drehung: -6,
  text: 'ZUR FREIGABE'
};
